import React, { useEffect, useRef, useState } from "react";
import "./EditProfile.css";
import { Button, Input, Modal } from "antd";
import Profileimage from "../../assets/images/profile.png";
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useAdminProfileEditMutation, useFileUploadsMutation } from "../../service/apiSlices/dashboard";
import editProfile from "../../assets/icons/edit-profile.svg";
import { EditProfileModalProps } from "../../utilities/types/common";
import { useTranslation } from "react-i18next";
import { uploadFile } from "../../utilities/utils";
import { fileConfigs } from "../../utilities/Constants/Constants";

// Extend Yup types
declare module 'yup' {
    interface StringSchema {
        noConsecutiveSpaces(): StringSchema;
    }
}

const EditProfileModal = ({ visible, onCancel, title, content, onConfirm, cancelText, okText, userProfileData }: EditProfileModalProps) => {
    const { t } = useTranslation("common");
    const [saveAdminProfile] = useAdminProfileEditMutation()
    const [uploadFileImage] = useFileUploadsMutation();
    const [imageFile, setImageFile] = useState<any>("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const formikRef = useRef<any>(null);

    useEffect(() => {
        if (userProfileData?.profile_image) {
            setImageFile(userProfileData?.profile_image);
        } else {
            setImageFile("");
        }
    }, [userProfileData?.profile_image, visible]);

    const initialValues = {
        firstName: userProfileData?.firstname ? userProfileData?.firstname : "",
        lastName: userProfileData?.lastname ? userProfileData?.lastname : "",
        companyName: userProfileData?.business_name ? userProfileData?.business_name : "",
        designation: userProfileData?.designation ? userProfileData?.designation : ""
    };

    // Custom test for consecutive spaces
    Yup.addMethod(Yup.string, 'noConsecutiveSpaces', function () {
        return this.test({
            name: 'noConsecutiveSpaces',
            message: `${t('profile.validation.consecutiveSpaceError')}`,
            test: value => !value || !value.includes('  ')
        });
    });

    const validationSchema = Yup.object({
        firstName: Yup.string().noConsecutiveSpaces().required(`${t('profile.required.firstName')}`).min(2, `${t('profile.validation.min.firstName')}`).matches(/^[a-zA-Z ]*$/, `${t('profile.validation.alphaNumericSpacesAllowed')}`),
        lastName: Yup.string().noConsecutiveSpaces().required(`${t('profile.required.lastName')}`).matches(/^[a-zA-Z ]*$/, `${t('profile.validation.alphaNumericSpacesAllowed')}`),
        designation: Yup.string().noConsecutiveSpaces().required(`${t('profile.required.designation')}`).matches(/^[a-zA-Z ]*$/, `${t('profile.validation.alphaNumericSpacesAllowed')}`),
        companyName: Yup.string().optional().matches(/^[a-zA-Z0-9 ]*$/, `${t('profile.validation.alphaNumericSpacesAllowed')}`)
    });

    const handleModalClose = () => {
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
        setImageFile("");
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onCancel();
    };

    const onSubmit = (values: typeof initialValues, { resetForm }: any) => {
        let payload = {
            first_name: values.firstName,
            last_name: values.lastName,
            image: imageFile,
            designation: values.designation,
            company_name: values.companyName
        }
        saveAdminProfile(payload).unwrap().then((response: any) => {
            if (response.status_code === 201) {
                onCancel()
            }

        })
            .catch((error: any) => { })
        resetForm();
    }

    const handleImageUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            await uploadFile(
                file,
                uploadFileImage,
                fileConfigs.image,
                (data) => setImageFile(data),
                (error) => console.error('Upload failed:', error)
            );
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveImage = () => {
        setImageFile("")
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    return (
        <div>
            <Modal
                className="edit-profile-modal"
                closable={userProfileData?.firstname ? true : false}
                centered
                open={visible}
                okText={okText}
                cancelText={cancelText}
                onOk={onConfirm}
                onCancel={handleModalClose}
                width={450}
                footer={false}
                maskClosable={false}
            >
                <div className="row">
                    <div className="col-md-12">
                        <h5 className="edit-profile-title">{t("profile.editProfile")}</h5>
                    </div>
                    <div className="col-md-12">
                        <div className="inner-content">
                            <div className="row g-4">
                                <div className="col-md-12 ps-0">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="profile-image-wrapper">
                                            <div className="profile-div position-relative">
                                                <img src={imageFile ? imageFile : Profileimage} alt="profile" className="profile-img" />
                                                <button className="profile-edit-btn" onClick={handleImageUpload}><img src={editProfile} alt="edit" /></button>
                                            </div>
                                            <input
                                                data-testid="file-input"
                                                type="file"
                                                ref={fileInputRef}
                                                className="d-none"
                                                accept="image/jpeg,image/png,image/gif"
                                                onChange={handleFileChange}
                                            />
                                            {imageFile && <p className="mb-0 remove-profile mt-2 text-center" onClick={handleRemoveImage}>{t("profile.removeProfile")}</p>}
                                        </div>
                                        <div className="profile-name">
                                            <h6 className="">{initialValues?.firstName} {initialValues?.lastName}</h6>
                                            <div className="profile-designation"><p>{initialValues?.designation}</p></div>
                                        </div>

                                    </div>
                                </div>
                                <div className="divider" />
                                <div className="col-md-12 p-0">
                                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize innerRef={formikRef}>
                                        {({ isSubmitting, setFieldValue, values, handleChange, handleSubmit, errors, touched, resetForm }) => (
                                            <Form className="edit-profile-form">
                                                <div className="row g-2">
                                                    <div className="col-md-6 mb-2">
                                                        <label htmlFor="firstName" className="label">{t("profile.firstName")}</label>
                                                        <Field
                                                            name="firstName"
                                                            as={Input}
                                                            minLength={2}
                                                            onChange={handleChange}
                                                            value={values.firstName}
                                                            placeholder={t("profile.placeholder.firstName")}
                                                            className="input"
                                                            status={touched.firstName && errors.firstName ? 'error' : ''}
                                                        />
                                                        <ErrorMessage
                                                            name="firstName"
                                                            component="div"
                                                            className="text-danger"
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <label htmlFor="lastName" className="label">{t("profile.lastName")}</label>
                                                        <Field
                                                            name="lastName"
                                                            as={Input}
                                                            onChange={handleChange}
                                                            value={values.lastName}
                                                            placeholder={t("profile.placeholder.lastName")}
                                                            className="input"
                                                            status={touched.lastName && errors.lastName ? 'error' : ''}
                                                        />
                                                        <ErrorMessage
                                                            name="lastName"
                                                            component="div"
                                                            className="text-danger"
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-2">
                                                        <label htmlFor="designation" className="label">{t("profile.designation")}</label>
                                                        <Field
                                                            name="designation"
                                                            as={Input}
                                                            onChange={handleChange}
                                                            value={values.designation}
                                                            placeholder={t("profile.placeholder.designation")}
                                                            className="input"
                                                            status={touched.designation && errors.designation ? 'error' : ''}
                                                        />
                                                        <ErrorMessage
                                                            name="designation"
                                                            component="div"
                                                            className="text-danger"
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-2">
                                                        <label htmlFor="companyName" className="label">{t("profile.organizationName")}</label>
                                                        <Field
                                                            name="companyName"
                                                            as={Input}
                                                            onChange={handleChange}
                                                            value={values.companyName}
                                                            placeholder={t("profile.placeholder.organizationName")}
                                                            className="input"
                                                            status={touched.companyName && errors.companyName ? 'error' : ''}
                                                        />
                                                        <ErrorMessage
                                                            name="companyName"
                                                            component="div"
                                                            className="text-danger"
                                                        />
                                                    </div>
                                                    <div className="col-md-12">
                                                        <div className="d-flex align-items-center justify-content-end gap-2 mt-4">
                                                            {userProfileData?.firstname ?
                                                                <Button className="modal-secondary-btn" onClick={() => handleModalClose()}>{t("buttons.cancel")}</Button>
                                                                :
                                                                ""
                                                            }
                                                            <Button className="modal-primary-btn" htmlType="submit">{t("buttons.saveChanges")}</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EditProfileModal;

